import { useState, useCallback } from "react";
import api from "@/api";
import { addHistoryEntry } from "@/PracticeHistory";
import { resolvePracticeFeeMode } from "@/lib/practiceBilling";

/**
 * Get current local date/time string in format YYYY-MM-DDTHH:MM
 * This ensures the datetime-local input shows the correct local time
 */
function getLocalDateTimeString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Custom hook for managing Clients CRUD operations
 */
export function useClients({ clients, setClients, setIsSaving }) {
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({
    fullName: "",
    taxCode: "",  // Codice Fiscale
    vatNumber: "",  // Partita IVA
    reaCode: "",  // N. iscrizione REA (Azienda + Ditta individuale)
    sdiCode: "",  // Codice SDI
    pec: "",  // PEC
    email: "",
    phone: "",
    address: "",  // Legacy field
    street: "",  // Indirizzo
    city: "",  // Città
    province: "",  // Provincia
    postalCode: "",  // CAP
    clientType: "Privato",
    managementType: "agenzia",  // Default: Gestione Agenzia
    notes: "",
    birthDate: "",  // Data di nascita
    birthPlace: "",  // Comune di nascita
    documentType: "",  // Tipo documento
    documentNumber: "",  // Numero documento
    documentIssuer: "",  // Ente rilascio
    documentIssueDate: "",  // Data rilascio
    documentExpiryDate: "",  // Data scadenza
    splitPaymentEnabled: false  // Split Payment art. 17-ter DPR 633/1972
  });

  const resetClientForm = useCallback(() => {
    setClientForm({
      fullName: "",
      firstName: "",
      lastName: "",
      taxCode: "",
      vatNumber: "",
      reaCode: "",
      sdiCode: "",
      pec: "",
      email: "",
      phone: "",
      address: "",
      street: "",
      city: "",
      province: "",
      postalCode: "",
      clientType: "Privato",
      managementType: "agenzia",  // Default: Gestione Agenzia
      notes: "",
      birthDate: "",
      birthPlace: "",
      documentType: "",
      documentNumber: "",
      documentIssuer: "",
      documentIssueDate: "",
      documentExpiryDate: "",
      splitPaymentEnabled: false
    });
    setEditingClient(null);
    setIsClientDialogOpen(false);
  }, []);

  const handleEditClient = useCallback((client) => {
    setEditingClient(client);
    // Backward compatibility: if firstName/lastName are missing on legacy
    // person-type records, split fullName at the first space so the new
    // separate fields show a sensible default value.
    const legacySplit = (() => {
      const isPersonType = ["Privato", "Ditta individuale", "Professionista"].includes(client.clientType || "");
      const hasSplit = client.firstName || client.lastName;
      if (!isPersonType || hasSplit) return { firstName: client.firstName || "", lastName: client.lastName || "" };
      const parts = String(client.fullName || "").trim().split(/\s+/);
      if (parts.length === 0) return { firstName: "", lastName: "" };
      return { lastName: parts[0] || "", firstName: parts.slice(1).join(" ") };
    })();
    setClientForm({
      fullName: client.fullName || "",
      firstName: legacySplit.firstName,
      lastName: legacySplit.lastName,
      taxCode: client.taxCode || "",
      vatNumber: client.vatNumber || "",
      reaCode: client.reaCode || "",
      sdiCode: client.sdiCode || "",
      pec: client.pec || "",
      email: client.email || "",
      phone: client.phone || "",
      address: client.address || "",
      street: client.street || "",
      city: client.city || "",
      province: client.province || "",
      postalCode: client.postalCode || "",
      clientType: client.clientType || "Privato",
      managementType: client.managementType || "agenzia",
      notes: client.notes || "",
      birthDate: client.birthDate || "",
      birthPlace: client.birthPlace || "",
      documentType: client.documentType || "",
      documentNumber: client.documentNumber || "",
      documentIssuer: client.documentIssuer || "",
      documentIssueDate: client.documentIssueDate || "",
      documentExpiryDate: client.documentExpiryDate || "",
      splitPaymentEnabled: !!client.splitPaymentEnabled
    });
    setIsClientDialogOpen(true);
  }, []);

  const handleSaveClient = useCallback(async () => {
    // For person types Cognome+Nome are required; for Azienda Ragione Sociale.
    const isPerson = ["Privato", "Ditta individuale", "Professionista"].includes(clientForm.clientType);
    if (isPerson) {
      if (!clientForm.lastName || !clientForm.firstName) {
        alert("Cognome e Nome sono obbligatori");
        return null;
      }
    } else if (!clientForm.fullName) {
      alert("La Ragione Sociale è obbligatoria");
      return null;
    }

    setIsSaving(true);
    try {
      // Always persist a canonical fullName composed from Cognome+Nome for person types.
      const payload = isPerson
        ? { ...clientForm, fullName: `${clientForm.lastName} ${clientForm.firstName}`.trim() }
        : clientForm;
      let savedClient;
      if (editingClient) {
        savedClient = await api.updateClient(editingClient.id, payload);
        setClients(prev => prev.map(c => c.id === editingClient.id ? savedClient : c));
      } else {
        savedClient = await api.createClient(payload);
        setClients(prev => [savedClient, ...prev]);
      }
      resetClientForm();
      return savedClient; // Return the saved client for auto-selection
    } catch (error) {
      alert(error.message || "Errore durante il salvataggio");
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [clientForm, editingClient, setClients, setIsSaving, resetClientForm]);

  const handleDeleteClient = useCallback(async (clientId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo cliente?")) {
      return;
    }

    try {
      await api.deleteClient(clientId);
      setClients(prev => prev.filter(c => c.id !== clientId));
    } catch (error) {
      alert(error.message || "Errore durante l'eliminazione");
    }
  }, [setClients]);

  return {
    // Dialog state
    isClientDialogOpen,
    setIsClientDialogOpen,
    editingClient,
    clientForm,
    setClientForm,

    // Actions
    handleEditClient,
    handleSaveClient,
    handleDeleteClient,
    resetClientForm
  };
}

/**
 * Custom hook for managing Cases/Practices CRUD operations
 */
export function useCases({ 
  cases, 
  setCases, 
  clients,
  invoices,
  setDeadlines,
  setIsSaving, 
  currentOperatorId, 
  numberingSequences,
  updateNumberingFromBackend,
  deadlineRules
}) {
  const [isCaseDialogOpen, setIsCaseDialogOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  
  // Fetch next number from backend (real DB-verified)
  const fetchNextCaseNumber = useCallback(async (practiceType, openingDate) => {
    try {
      let year = null;
      if (practiceType === "Gestionale Agenzia" && openingDate) {
        const dateStr = String(openingDate);
        if (dateStr.length >= 4) year = parseInt(dateStr.slice(0, 4), 10);
      }
      const data = await api.getNextNumber(practiceType, year);
      return data.caseNumber;
    } catch {
      // Fallback: use local sequences
      const prefix = practiceType === "Gestionale Studio" ? "GS" : "GA";
      const currentNum = (numberingSequences[practiceType] || 0) + 1;
      return `${prefix}-${String(currentNum).padStart(4, "0")}`;
    }
  }, [numberingSequences]);

  // Sync wrapper for immediate use (returns a temporary value, then updates async)
  const generatePreviewCaseNumberSync = useCallback((practiceType) => {
    const prefix = practiceType === "Gestionale Studio" ? "GS" : "GA";
    const currentNum = (numberingSequences[practiceType] || 0) + 1;
    return `${prefix}-${String(currentNum).padStart(4, "0")}`;
  }, [numberingSequences]);

  const [caseForm, setCaseForm] = useState({
    subject: "",
    clientId: "",
    requestChannel: "Telefono",
    practiceType: "Gestionale Agenzia",
    practiceArea: "",
    enteCoinvolto: "",
    description: "",
    assignedOperatorId: currentOperatorId ? String(currentOperatorId) : "none",
    operatorHistory: [],
    priority: "Media",
    status: "Nuova richiesta",
    dueDate: "",
    openingDate: getLocalDateTimeString(),
    openingDateConfirmed: false,
    caseNumber: "",
    isNumberLocked: false,
    requestedDocuments: "",
    estimatedFee: "",
    feeMode: "lordo",
    importoDirittiBolli: "",
    internalNotes: "",
    perContoDi: null
  });

  const initNewCaseForm = useCallback(async () => {
    const now = getLocalDateTimeString();
    let previewNumber;
    try {
      previewNumber = await fetchNextCaseNumber("Gestionale Agenzia", now);
    } catch {
      previewNumber = generatePreviewCaseNumberSync("Gestionale Agenzia");
    }
    
    // Calculate default due date: opening date + 10 calendar days
    const openingDate = new Date(now);
    const defaultDueDate = new Date(openingDate);
    defaultDueDate.setDate(defaultDueDate.getDate() + 10);
    const dueDateStr = defaultDueDate.toISOString().split('T')[0];
    
    setCaseForm({
      subject: "",
      clientId: "",
      requestChannel: "Telefono",
      practiceType: "Gestionale Agenzia",
      practiceArea: "",
      enteCoinvolto: "",
      description: "",
      assignedOperatorId: currentOperatorId ? String(currentOperatorId) : "none",
      operatorHistory: [],
      priority: "Media",
      status: "Nuova richiesta",
      dueDate: dueDateStr,
      openingDate: now,
      openingDateConfirmed: false,
      caseNumber: previewNumber,
      isNumberLocked: false,
      requestedDocuments: "",
      estimatedFee: "",
      feeMode: "lordo",
      importoDirittiBolli: "",
      internalNotes: "",
      perContoDi: null
    });
    setEditingCase(null);
  }, [currentOperatorId, fetchNextCaseNumber, generatePreviewCaseNumberSync]);

  const resetCaseForm = useCallback(() => {
    setEditingCase(null);
    setIsCaseDialogOpen(false);
  }, []);

  // State for case history
  const [caseHistory, setCaseHistory] = useState([]);

  const handleEditCase = useCallback(async (caseItem) => {
    setEditingCase(caseItem);
    setCaseForm({
      subject: caseItem.subject || "",
      clientId: caseItem.clientId || "",
      requestChannel: caseItem.requestChannel || "Telefono",
      practiceType: caseItem.practiceType || "Gestionale Studio",
      practiceArea: caseItem.practiceArea || "",
      enteCoinvolto: caseItem.enteCoinvolto || "",
      description: caseItem.description || "",
      assignedOperatorId: caseItem.assignedOperatorId ? String(caseItem.assignedOperatorId) : "none",
      operatorHistory: caseItem.operatorHistory || [],
      priority: caseItem.priority || "Media",
      status: caseItem.status || "Nuova richiesta",
      dueDate: caseItem.dueDate || "",
      openingDate: caseItem.openingDate || new Date().toISOString().slice(0, 16),
      openingDateConfirmed: true,
      caseNumber: caseItem.caseNumber || "",
      isNumberLocked: caseItem.isNumberLocked || true,
      requestedDocuments: caseItem.requestedDocuments?.join(", ") || "",
      estimatedFee: caseItem.estimatedFee || "",
      feeMode: resolvePracticeFeeMode(caseItem, invoices, clients),
      // Unified field: fallback to legacy diritti + bolli sum for records saved before migration
      importoDirittiBolli: caseItem.importoDirittiBolli
        || ((Number(caseItem.importoDiritti) || 0) + (Number(caseItem.importoBolli) || 0))
        || "",
      internalNotes: caseItem.internalNotes || "",
      structuredNotes: caseItem.structuredNotes || [],
      perContoDi: caseItem.perContoDi || null
    });
    setIsCaseDialogOpen(true);
    
    // Fetch case history
    try {
      const history = await api.getCaseHistory(caseItem.id);
      setCaseHistory(history || []);
    } catch (err) {
      console.error("Failed to fetch case history:", err);
      setCaseHistory([]);
    }
  }, [invoices, clients]);

  const handleSaveCase = useCallback(async () => {
    if (!caseForm.subject || !caseForm.clientId) {
      alert("Oggetto e cliente sono obbligatori");
      return;
    }

    // Tipo Pratica è obbligatorio solo per Gestionale Studio
    if (caseForm.practiceType === "Gestionale Studio" && (!caseForm.practiceArea || caseForm.practiceArea.trim() === "")) {
      alert("Tipo Pratica è obbligatorio per Gestionale Studio");
      return;
    }

    // Ente Coinvolto è obbligatorio
    if (!caseForm.enteCoinvolto || caseForm.enteCoinvolto.trim() === "") {
      alert("Ente Coinvolto è obbligatorio");
      return;
    }

    if (!caseForm.practiceType) {
      alert("Tipologia Gestionale è obbligatoria");
      return;
    }

    // Scadenza Pratica è obbligatoria
    if (!caseForm.dueDate) {
      alert("Scadenza Pratica è obbligatoria");
      return;
    }

    // "Per conto di" validation for company clients
    if (caseForm.perContoDi && clients) {
      const selectedClient = clients.find(c => c.id === caseForm.clientId);
      const isCompany = selectedClient && ["Azienda", "Ditta individuale"].includes(selectedClient.clientType);
      if (isCompany) {
        const pcd = caseForm.perContoDi;
        if (!pcd.email && !pcd.cellulare) {
          alert("Inserire almeno Email o Cellulare nella sezione 'Per conto di'");
          return;
        }
      }
    }

    const documents = caseForm.requestedDocuments
      ? caseForm.requestedDocuments.split(",").map((d) => d.trim()).filter(Boolean)
      : [];

    setIsSaving(true);
    try {
      if (editingCase) {
        const oldStatus = editingCase.status;
        const newStatus = caseForm.status;

        const updateData = {
          caseNumber: editingCase.caseNumber,
          practiceType: editingCase.practiceType,
          openingDate: editingCase.openingDate,
          openingDateConfirmed: true,
          subject: caseForm.subject,
          clientId: caseForm.clientId,
          requestChannel: caseForm.requestChannel,
          practiceArea: caseForm.practiceArea,
          enteCoinvolto: caseForm.enteCoinvolto || "",
          description: caseForm.description,
          assignedOperatorId: caseForm.assignedOperatorId && caseForm.assignedOperatorId !== "none" ? caseForm.assignedOperatorId : null,
          operatorHistory: caseForm.operatorHistory || [],
          creatorOperatorId: editingCase.creatorOperatorId,
          priority: caseForm.priority,
          status: caseForm.status,
          dueDate: caseForm.dueDate,
          requestedDocuments: documents,
          receivedDocuments: editingCase.receivedDocuments || [],
          estimatedFee: Number(caseForm.estimatedFee) || 0,
          finalFee: editingCase.finalFee,
          feeMode: caseForm.feeMode === "netto" ? "netto" : "lordo",
          importoDirittiBolli: Number(caseForm.importoDirittiBolli) || 0,
          internalNotes: caseForm.internalNotes,
          isNumberLocked: true,
          perContoDi: caseForm.perContoDi || null
        };

        const updated = await api.updateCase(editingCase.id, updateData);
        setCases(prev => prev.map(c => c.id === editingCase.id ? updated : c));

        // History entries (status change, operator change, field changes) are now tracked by the backend
        
        // Update editingCase with fresh data
        setEditingCase(updated);
        return updated; // Stay in form, return updated case
      } else {
        const newCaseData = {
          caseNumber: "PENDING",
          practiceType: caseForm.practiceType,
          openingDate: caseForm.openingDate || new Date().toISOString(),
          openingDateConfirmed: caseForm.openingDateConfirmed || false,
          subject: caseForm.subject,
          clientId: caseForm.clientId,
          requestChannel: caseForm.requestChannel,
          practiceArea: caseForm.practiceArea,
          enteCoinvolto: caseForm.enteCoinvolto || "",
          description: caseForm.description,
          creatorOperatorId: currentOperatorId,
          assignedOperatorId: caseForm.assignedOperatorId && caseForm.assignedOperatorId !== "none" ? caseForm.assignedOperatorId : currentOperatorId,
          operatorHistory: caseForm.operatorHistory || [],
          priority: caseForm.priority,
          status: caseForm.status,
          dueDate: caseForm.dueDate,
          requestedDocuments: documents,
          receivedDocuments: [],
          estimatedFee: Number(caseForm.estimatedFee) || 0,
          finalFee: null,
          feeMode: caseForm.feeMode === "netto" ? "netto" : "lordo",
          importoDirittiBolli: Number(caseForm.importoDirittiBolli) || 0,
          internalNotes: caseForm.internalNotes,
          isNumberLocked: true,
          perContoDi: caseForm.perContoDi || null,
          // 27/02/2026 — Linkage Diario → Pratica: propaga sourceModule
          // e sourceContactId al backend, che appenderà un audit entry
          // alla nota Diario di origine per la traceability bidirezionale.
          sourceModule: caseForm.sourceModule || null,
          sourceContactId: caseForm.sourceContactId || null,
        };

        const created = await api.createCase(newCaseData);
        setCases(prev => [created, ...prev]);
        updateNumberingFromBackend(caseForm.practiceType, created.caseNumber);

        // 27/02/2026 — Se la pratica nasce dal Diario, notifica il
        // Diario di ricaricare i contatti così la nota mostra subito
        // il badge "Convertita in pratica" appena scritto lato backend.
        if (newCaseData.sourceModule === "diario" && newCaseData.sourceContactId) {
          try { window.dispatchEvent(new CustomEvent("gi:diary-refresh")); } catch (_) {}
        }

        // History entry is now created by the backend automatically

        // Generate auto deadlines if rules exist
        if (deadlineRules && deadlineRules[caseForm.practiceArea]) {
          const rules = deadlineRules[caseForm.practiceArea];
          const today = new Date();
          const autoDeadlines = rules.map((rule) => ({
            caseId: created.id,
            title: rule.title || rule.name,
            description: rule.description || "",
            dueDate: new Date(today.getTime() + rule.days * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
            status: "in_sospeso",
            priority: rule.priority || "Media"
          }));

          try {
            const deadlinePromises = autoDeadlines.map(d => api.createDeadline(d));
            const createdDeadlines = await Promise.all(deadlinePromises);
            setDeadlines(prev => [...prev, ...createdDeadlines]);
          } catch (err) {
            console.error("Error creating deadlines:", err);
          }
        }
        
        // Update caseForm with the created case data (for printing etc.)
        // Also set editingCase so form knows it's now editing
        setEditingCase(created);
        setCaseForm(prev => ({
          ...prev,
          id: created.id,
          caseNumber: created.caseNumber,
          isNumberLocked: true
        }));
        
        return created; // Return created case for caller to use
      }
      
      // For editing, don't reset - stay in form
      return editingCase;
    } catch (error) {
      alert(error.message || "Errore durante il salvataggio");
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [caseForm, editingCase, setCases, setDeadlines, setIsSaving, currentOperatorId, updateNumberingFromBackend, deadlineRules, setEditingCase, setCaseForm]);

  const handleDeleteCase = useCallback(async (caseId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa pratica?")) {
      return;
    }

    try {
      await api.deleteCase(caseId);
      setCases(prev => prev.filter(c => c.id !== caseId));
    } catch (error) {
      alert(error.message || "Errore durante l'eliminazione");
    }
  }, [setCases]);

  const updateCaseStatus = useCallback(async (caseId, newStatus) => {
    const caseItem = cases.find(c => c.id === caseId);
    if (!caseItem) return;

    const oldStatus = caseItem.status;
    if (oldStatus === newStatus) return;

    try {
      const updated = await api.updateCase(caseId, { ...caseItem, status: newStatus });
      setCases(prev => prev.map(c => c.id === caseId ? updated : c));

      // History entry is now created by the backend automatically
    } catch (error) {
      console.error("Error updating status:", error);
    }
  }, [cases, setCases, currentOperatorId]);

  const handleQuickStatusChange = useCallback(async (caseId, newStatus) => {
    await updateCaseStatus(caseId, newStatus);
  }, [updateCaseStatus]);

  return {
    // Dialog state
    isCaseDialogOpen,
    setIsCaseDialogOpen,
    editingCase,
    caseForm,
    setCaseForm,
    caseHistory,

    // Actions
    handleEditCase,
    handleSaveCase,
    handleDeleteCase,
    handleQuickStatusChange,
    resetCaseForm,
    initNewCaseForm,
    generatePreviewCaseNumberSync,
    fetchNextCaseNumber
  };
}
